/**
 * The workflow badges in the PO Line Detail modal header: who owes the next
 * move, and the acknowledgement status.
 *
 * Moved out of POLineDetailModal for Customer review 11 Sep (item 28) so
 * both read the same pendingProposal predicates as the Change Requests tab.
 */
import type { ReactNode } from 'react';
import type { PurchaseOrderLine } from '../../lib/api';
import { isPhantomChangeRequest } from './pendingProposal';

interface POLineHeaderBadgesProps {
  line: PurchaseOrderLine;
  /** Rendered between the two badges — the header's existing order (CR-6510 concession badge). */
  children?: ReactNode;
}

export default function POLineHeaderBadges({ line, children }: POLineHeaderBadgesProps) {
  const status = line.acknowledge_status;
  if (!status) return <>{children}</>;
  // CR-4882: a change_proposed_* status with no supplier_proposed_* value is
  // a stuck legacy row; treat it as resolved so no phantom owner is claimed.
  const isStuck = isPhantomChangeRequest(line);

  // CR-4704 round 4: "Waiting on Supplier" / "Waiting on Customer", matching
  // the PO Lines table. A `new` line is Supplier's because the supplier owes
  // the order acknowledgement (accept_delivery_date's initial accept) — not
  // because a change is pending. Customer review 11 Sep (item 28): an
  // imported supplier_proposed_date on such a line changes nothing here.
  let owner: 'Customer' | 'Supplier' | null = null;
  if (!isStuck) {
    if (status === 'change_proposed_supplier') owner = 'Customer';
    else if (status === 'change_proposed_customer') owner = 'Supplier';
    else if (status === 'new') owner = 'Supplier';
  }

  // CR-4859: 'accepted' shows as WIP, matching the PO Lines table column.
  const effectiveStatus = isStuck ? 'accepted' : status;
  const isWip = effectiveStatus === 'accepted' || effectiveStatus === 'wip';
  const isChanged = effectiveStatus.startsWith('change_proposed');
  const statusClass = isWip ? 'bg-green-100 text-green-800 border border-green-300'
    : isChanged ? 'bg-amber-100 text-amber-800 border border-amber-300'
    : effectiveStatus === 'change_declined' ? 'bg-red-100 text-red-800 border border-red-300'
    : 'bg-gray-100 text-gray-800 border border-gray-300';
  const statusLabel = isWip ? 'WIP'
    : isChanged ? 'Changes Pending'
    : effectiveStatus === 'change_declined' ? 'Change Declined'
    : effectiveStatus;

  return (
    <>
      {owner && (
        <span className="px-3 py-1 rounded-full font-semibold text-xs bg-blue-100 text-blue-800 border border-blue-300">
          Waiting on {owner}
        </span>
      )}
      {children}
      {status !== 'new' && (
        <span className={`px-3 py-1 rounded-full font-semibold text-xs flex items-center gap-1 ${statusClass}`}>
          {statusLabel}
        </span>
      )}
    </>
  );
}
